import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  useCategories,
  useLocations,
  useManufacturers,
  useModels,
  useStatuses,
  useSuppliers,
  type NamedEntity,
} from '../lib/queries';
import { PageHeader, Spinner, cn } from '../components/ui';

type TabKey = 'models' | 'categories' | 'manufacturers' | 'locations' | 'suppliers' | 'statuses';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'models', label: 'Modelle' },
  { key: 'categories', label: 'Kategorien' },
  { key: 'manufacturers', label: 'Hersteller' },
  { key: 'locations', label: 'Standorte' },
  { key: 'suppliers', label: 'Lieferanten' },
  { key: 'statuses', label: 'Status' },
];

/** Stammdaten ansehen und pflegen. */
export function StammdatenPage() {
  const auth = useAuth();
  const [tab, setTab] = useState<TabKey>('models');
  const [onlyUsed, setOnlyUsed] = useState(false);

  const models = useModels({ only_used: onlyUsed || undefined });
  const categories = useCategories();
  const manufacturers = useManufacturers();
  const locations = useLocations();
  const suppliers = useSuppliers();
  const statuses = useStatuses();

  const canEdit = auth.can('stammdaten.write');

  return (
    <div>
      <PageHeader
        eyebrow="Grundlagen"
        title="Stammdaten"
        subtitle="Modelle, Kategorien, Hersteller, Standorte und Lieferanten."
      />

      <div className="mb-5 flex flex-wrap gap-1.5" role="tablist">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            role="tab"
            aria-selected={tab === entry.key}
            onClick={() => setTab(entry.key)}
            className={cn(
              'rounded-[var(--radius-pill)] border px-3.5 py-1.5 text-sm transition-colors duration-150',
              tab === entry.key
                ? 'border-[var(--color-khadi-forest-800)] bg-[var(--color-khadi-forest-800)] font-semibold text-[var(--color-khadi-cream-50)]'
                : 'border-[var(--color-line)] text-[var(--color-fg2)] hover:bg-[var(--color-bg-tinted)]',
            )}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === 'models' && (
        <section className="surface animate-fade-up overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--color-line)] px-5 py-4">
            <p className="text-sm text-[var(--color-fg3)]">
              {models.data?.rows.length ?? 0} Modelle
            </p>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={onlyUsed}
                onChange={(event) => setOnlyUsed(event.target.checked)}
                className="h-4 w-4 accent-[var(--color-accent)]"
              />
              Nur Modelle mit Geräten
            </label>
          </div>

          {models.isLoading && <Spinner />}

          <ul className="divide-y divide-[var(--color-line)]">
            {models.data?.rows.map((model) => (
              <li key={model.id} className="flex items-center justify-between gap-3 px-5 py-3.5">
                <span className="min-w-0">
                  <span className="block truncate text-[0.9375rem]">{model.name}</span>
                  <span className="block truncate text-sm text-[var(--color-fg3)]">
                    {model.manufacturer?.name ?? 'ohne Hersteller'} ·{' '}
                    {model.category?.name ?? 'ohne Kategorie'}
                    {model.model_number ? ` · ${model.model_number}` : ''}
                  </span>
                </span>
                <span
                  className={cn(
                    'tabular shrink-0 text-sm',
                    (model.assets_count ?? 0) > 0
                      ? 'text-[var(--color-fg2)]'
                      : 'text-[var(--color-fg3)] italic',
                  )}
                >
                  {model.assets_count ?? 0}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {tab === 'statuses' && (
        <section className="surface animate-fade-up overflow-hidden">
          <ul className="divide-y divide-[var(--color-line)]">
            {statuses.data?.rows.map((status) => (
              <li key={status.id} className="flex items-center justify-between gap-3 px-5 py-3.5">
                <span>
                  <span className="text-[0.9375rem]">{status.name}</span>
                  {status.is_archived && (
                    <span className="ml-2 text-sm text-[var(--color-fg3)]">ausgemustert</span>
                  )}
                </span>
                <span className="tabular text-sm text-[var(--color-fg2)]">
                  {status.assets_count ?? 0}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {tab === 'categories' && (
        <SimpleList path="categories" title="Kategorie" data={categories.data?.rows} canEdit={canEdit} />
      )}
      {tab === 'manufacturers' && (
        <SimpleList
          path="manufacturers"
          title="Hersteller"
          data={manufacturers.data?.rows}
          canEdit={canEdit}
        />
      )}
      {tab === 'locations' && (
        <SimpleList path="locations" title="Standort" data={locations.data?.rows} canEdit={canEdit} />
      )}
      {tab === 'suppliers' && (
        <SimpleList path="suppliers" title="Lieferant" data={suppliers.data?.rows} canEdit={canEdit} />
      )}
    </div>
  );
}

/** Gleicher Aufbau für Kategorien, Hersteller, Standorte und Lieferanten. */
function SimpleList({
  path,
  title,
  data,
  canEdit,
}: {
  path: string;
  title: string;
  data?: NamedEntity[];
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState<{ id: number; name: string } | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [path] });

  const create = useMutation({
    mutationFn: (name: string) => api(`/${path}`, { method: 'POST', body: { name } }),
    onSuccess: () => {
      void invalidate();
      setNewName('');
      toast.success(`${title} angelegt`);
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Anlegen fehlgeschlagen'),
  });

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      api(`/${path}/${id}`, { method: 'PATCH', body: { name } }),
    onSuccess: () => {
      void invalidate();
      setEditing(null);
      toast.success('Gespeichert');
    },
  });

  return (
    <section className="surface animate-fade-up overflow-hidden">
      {canEdit && (
        <form
          className="flex gap-2 border-b border-[var(--color-line)] px-5 py-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (newName.trim()) create.mutate(newName.trim());
          }}
        >
          <input
            className="field"
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            placeholder={`${title} hinzufügen`}
            aria-label={`${title} hinzufügen`}
          />
          <button type="submit" className="btn btn-primary btn-md shrink-0" disabled={!newName.trim()}>
            <Plus className="h-4 w-4" aria-hidden />
          </button>
        </form>
      )}

      <ul className="divide-y divide-[var(--color-line)]">
        {data?.map((entry) => (
          <li key={entry.id} className="flex items-center justify-between gap-3 px-5 py-3.5">
            {editing?.id === entry.id ? (
              <div className="flex flex-1 gap-2">
                <input
                  autoFocus
                  className="field"
                  value={editing.name}
                  onChange={(event) => setEditing({ ...editing, name: event.target.value })}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') rename.mutate(editing);
                    if (event.key === 'Escape') setEditing(null);
                  }}
                />
                <button
                  type="button"
                  onClick={() => rename.mutate(editing)}
                  className="btn btn-primary btn-md"
                  aria-label="Speichern"
                >
                  <Check className="h-4 w-4" aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(null)}
                  className="btn btn-ghost btn-md"
                  aria-label="Abbrechen"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  disabled={!canEdit}
                  onClick={() => setEditing({ id: entry.id, name: entry.name })}
                  className="flex-1 text-left text-[0.9375rem] disabled:cursor-default"
                >
                  {entry.name}
                </button>
                <span className="tabular shrink-0 text-sm text-[var(--color-fg3)]">
                  {entry.assets_count ?? 0}
                </span>
              </>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
